import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { EmptyState, Flex, Label, Separator, Text } from '@raystack/apsara';
import _ from 'lodash';

import { Header } from '@src/components/Custom/Header';
import MultiRangeSlider from '@src/components/MultiRangeSlider/MultiRangeSlider';
import { ScrollView } from '@src/components/PixxelUIKit/ScrollView';
import * as Skeleton from '@src/components/PixxelUIKit/Skeleton';
import { Show } from '@src/hoc';
import useConstellations from '@src/hooks/useConstellation';
import {
  setCloudCoverage,
  setOffNadirAngle,
  setSatellite,
  setShowAngle,
  setShowCloudCoverage,
  setShowGSD,
  setSpatialResolution
} from '@src/redux/_reducers/filtersReducer';
import { DEFAULT_FILTER_VALUES } from '@src/utils/constants/EXPLORE_CONSTANTS';
import {
  CONSTELLATION_IDS_CONSTS,
  SENTINEL_2_FILTER_DATA
} from '@src/utils/constants/LENS_CONSTANTS';

import { DateRangePicker } from './DateRangePicker';
import { Satellite } from './Satellite';
import { SatelliteInfo } from './SatelliteInfo';

import styles from './Filter.module.css';

function ShowSatellites({ isLoading, error, constellations }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!isLoading && ref.current) {
      setTimeout(() => {
        if (ref.current) {
          ref.current.scrollTop = 0;
        }
      });
    }
  }, [isLoading]);

  const getSatelliteInfo = id => {
    return SatelliteInfo.find(info => info.id === id);
  };

  return (
    <div
      ref={ref}
      style={{
        padding: 'var(--rs-space-4) var(--rs-space-5) 0',
        borderRadius: 'unset',
        height: 248,
        overflow: 'hidden scroll'
      }}
    >
      {!error && <Label size='medium'>Satellites</Label>}

      {isLoading ? (
        <Skeleton.SatelliteList />
      ) : (
        <>
          {error ? (
            <EmptyState
              icon={<ExclamationTriangleIcon />}
              heading='Error Fetching Data'
              subHeading="We're trying to get it resolved from our end, please try again later sometime."
            />
          ) : (
            <Flex
              direction='column'
              style={{
                gap: 'var(--rs-space-4)',
                padding: 'var(--rs-space-4) 0'
              }}
            >
              {constellations.map((constellation, i) => (
                <Satellite
                  constellation={constellation}
                  key={i}
                  info={getSatelliteInfo(constellation.id)}
                />
              ))}
            </Flex>
          )}
        </>
      )}
    </div>
  );
}

export default function NewFilter({ setShowFilters, showFilters, images }) {
  // redux states
  const filters = useSelector(state => state.filters);
  const filterStates = useSelector(state => state.filters.filterStates);

  const [queryables, setQueryables] = useState([]);

  const dispatch = useDispatch();

  // updated eligible queryables
  useEffect(() => {
    if (filters.satellite.length > 0) {
      if (filters.satellite.length === 1) {
        dispatch(setShowGSD(false));
      }
      let data = [];
      filters.satellite.map(item => {
        item?.queryables?.map(queryable => {
          if (!data.some(p => p === queryable)) {
            data.push(queryable);
          }
        });
      });
      setQueryables(data);
    } else setQueryables([]);
  }, [dispatch, filters.satellite]);

  // Fetch constellation using SWR
  const { isLoading, error, constellations, sentinel2Data } =
    useConstellations();

  // autoScroll to selected satellite
  useEffect(() => {
    if (constellations && constellations.length > 0) {
      var elem =
        document.getElementById(CONSTELLATION_IDS_CONSTS.SENTINEL_2) ||
        document.getElementById('Sentinel-2');
      if (elem !== null) {
        elem.scrollIntoView();
      }
    }
  }, [constellations]);

  // set sentinel-2 data is default only if there are no selected satellites
  useEffect(() => {
    if (!_.isEmpty(sentinel2Data) && filters?.satellite?.length === 0) {
      dispatch(
        setSatellite([
          { ...SENTINEL_2_FILTER_DATA, satellite_id: sentinel2Data.id }
        ])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinel2Data]);

  return (
    <Show when={showFilters}>
      <div className={styles.FilterDiv}>
        <Header onClose={() => setShowFilters(false)}>
          <Header.Text size='medium'>Filters</Header.Text>
        </Header>

        <Flex direction='column' className={styles.dateWrapper}>
          <Text size={2}>Date Range</Text>

          <DateRangePicker filters={filters} />
        </Flex>

        <Separator />

        <ScrollView height={'100%'}>
          <ShowSatellites
            constellations={constellations}
            isLoading={isLoading}
            error={error}
          />

          <div>
            {queryables.length > 0 ? (
              <>
                {queryables.map(queryable => {
                  switch (queryable) {
                    case 'eo:cloud_cover':
                      return (
                        <MultiRangeSlider
                          min={DEFAULT_FILTER_VALUES.cloud_coverage[0]}
                          max={DEFAULT_FILTER_VALUES.cloud_coverage[1]}
                          name='Cloud Coverage'
                          curMin={filters.cloudCoverage[0]}
                          curMax={filters.cloudCoverage[1]}
                          onChange={e => dispatch(setCloudCoverage(e))}
                          units='%'
                          checked={filterStates.showCloudCoverage}
                          setChecked={() =>
                            dispatch(
                              setShowCloudCoverage(
                                !filterStates.showCloudCoverage
                              )
                            )
                          }
                          data-test-id='cloud-coverage-slider'
                        />
                      );
                    case 'gsd':
                      return (
                        <MultiRangeSlider
                          min={DEFAULT_FILTER_VALUES.spatial_gsd[0]}
                          max={DEFAULT_FILTER_VALUES.spatial_gsd[1]}
                          name='Spatial Resolution (GSD)'
                          curMin={filters.spatialResolution[0]}
                          curMax={filters.spatialResolution[1]}
                          onChange={e => dispatch(setSpatialResolution(e))}
                          units='m'
                          checked={filterStates.showGSD}
                          setChecked={() =>
                            dispatch(setShowGSD(!filterStates.showGSD))
                          }
                          data-test-id='gsd-slider'
                          showToggle={
                            filters.satellite.length === 1 && images.length > 0
                              ? false
                              : true
                          }
                          staticVal={`${images[0]?.gsd} m`}
                        />
                      );
                    case 'off_nadir':
                      return (
                        <MultiRangeSlider
                          min={DEFAULT_FILTER_VALUES.off_nadir_angle[0]}
                          max={DEFAULT_FILTER_VALUES.off_nadir_angle[1]}
                          name='Off-Nadir angle'
                          curMin={filters.offNadirAngle[0]}
                          curMax={filters.offNadirAngle[1]}
                          onChange={e => dispatch(setOffNadirAngle(e))}
                          units={<span>&#176;</span>}
                          checked={filterStates.showAngle}
                          setChecked={() =>
                            dispatch(setShowAngle(!filterStates.showAngle))
                          }
                          data-test-id='off-nadir-slider'
                        />
                      );
                  }
                })}
              </>
            ) : (
              <Label size='medium' style={{ padding: '12px' }}>
                Please select a constellation that supports filters
              </Label>
            )}
          </div>
        </ScrollView>
      </div>
    </Show>
  );
}
